import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { EMPTY, Observable, PartialObserver, Subject, catchError, filter, finalize, switchMap, take, tap } from 'rxjs';

import { CartState, IdAndHash } from '@fcom/dapi';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { snapshot } from '@fcom/rx';
import { noop } from '@fcom/core/utils';
import { FinnairCart, FinnairServiceRequestItem, SubCategory } from '@fcom/dapi/api/models';
import { BookingAppState, PaxDetailsState } from '@fcom/common/interfaces/booking';
import { QueueService } from '@fcom/common-booking/services/queue.service';
import { seatMapStatusByFlightSelector } from '@fcom/common-booking/store/selectors/seat-map.selector';

import { CartActions } from '../store/actions';
import { AncillaryService } from '../modules/ancillaries/services/ancillary.service';
import { getUpdateId, PASSENGERS_CART_PART } from '../utils';
import {
  cartFlights,
  cartIdAndHash,
  cartSeatServicesCanBeSoldByFlightSelector,
  cartState,
  cartUrl,
} from '../store/selectors';
import { BookingCartService } from './booking-cart.service';

@Injectable()
export class BookingQueueService {
  private cartService = inject(BookingCartService);
  private ancillaryService = inject(AncillaryService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private queueService = inject(QueueService);

  private seatsCanBeSoldByFlight = this.store$.selectSignal(cartSeatServicesCanBeSoldByFlightSelector);
  private seatMapStatusByFlight = this.store$.selectSignal(seatMapStatusByFlightSelector);

  private cancelCartCreate$ = new Subject<void>();
  private cancelCartUpdate$ = new Subject<void>();
  private cancelPaxUpdate$ = new Subject<void>();
  private cancelSeatMapSubjects: Record<string, Subject<void>> = {};

  queueCartCreate(selectedOfferId: string, locale: string, offersHash: string): void {
    // TODO: We should remember old offer selected so we can skip new cart creation when using the same offer.
    // For now we just create the cart again when offer is selected.
    const queueId = [selectedOfferId, locale, offersHash].join('_');
    this.queueService.startQueue(queueId);

    // We need to cancel any pax updates to previous carts
    this.cancelCartCreate$.next();
    this.cancelCartUpdate$.next();
    this.cancelPaxUpdate$.next();
    this.queueService.cancelServiceAvailability$.next();
    this.queueService.cancelPayment.next();

    const cancelStream$ = this.queueService.toCancelEvent(this.cancelCartCreate$);
    this.queueService.sendToQueue(
      'createCart',
      this.cartService.triggerCreateCart(selectedOfferId, offersHash, cancelStream$),
      cancelStream$
    );
  }

  queueCartCreateByBounds(selectedAirBoundIds: string[], locale: string, boundsHash: string): void {
    // TODO: We should remember old offer selected so we can skip new cart creation when using the same offer.
    // For now we just create the cart again when offer is selected.
    const queueId = [selectedAirBoundIds.join('_'), locale, boundsHash].join('_');
    this.queueService.startQueue(queueId);

    // We need to cancel any pax updates to previous carts
    this.cancelCartCreate$.next();
    this.cancelCartUpdate$.next();
    this.cancelPaxUpdate$.next();
    this.queueService.cancelServiceAvailability$.next();
    this.queueService.cancelPayment.next();

    const cancelStream$ = this.queueService.toCancelEvent(this.cancelCartCreate$);
    this.queueService.sendToQueue(
      'createCart',
      this.cartService.triggerCreateCartByBounds(selectedAirBoundIds, boundsHash, cancelStream$),
      cancelStream$
    );
  }

  queuePassengersAndCorporateCodeUpdate(passengersAndCorporateCode: PaxDetailsState): void {
    this.cancelCartUpdate$.next();
    this.queueService.cancelPayment.next();
    this.store$.dispatch(CartActions.isUpdatingPartialCart({ cartPartIds: PASSENGERS_CART_PART }));

    const cancelStream$ = this.queueService.toCancelEvent(this.cancelCartUpdate$);
    this.queueService.sendToQueue(
      'paxDetailsAndCorporateCodeUpdate',
      this.fromCartState((cart: CartState) => {
        const persistedCartUpdateExist = Boolean(
          cart.cartData?.passengers &&
          cart.cartData?.passengers.length > 0 &&
          cart.cartData?.passengers.find((t) => !!t.firstName)
        );

        return this.cartService
          .triggerTravelersAndCorporateCodeUpdate(
            cart.cartUrl,
            passengersAndCorporateCode,
            cancelStream$,
            persistedCartUpdateExist
          )
          .pipe(
            finalize(() =>
              this.store$.dispatch(CartActions.clearUpdatingPartialCart({ cartPartIds: PASSENGERS_CART_PART }))
            )
          );
      }),
      cancelStream$
    );
  }

  cancelAllSeatMapFetches(): void {
    Object.values(this.cancelSeatMapSubjects).forEach((subject) => subject.next());
  }

  queueTriggerFetchSeatMap(flightId: string, currencyCode?: string): void {
    const seatsCanBeSold = this.seatsCanBeSoldByFlight()[flightId] ?? true;
    const status = this.seatMapStatusByFlight()[flightId];
    if (seatsCanBeSold && status !== SeatMapStatus.OK) {
      if (!this.cancelSeatMapSubjects[flightId]) {
        this.cancelSeatMapSubjects[flightId] = new Subject<void>();
      } else {
        this.cancelSeatMapSubjects[flightId].next();
      }
      const cancelStream$ = this.queueService.toCancelEvent(this.cancelSeatMapSubjects[flightId]);
      this.queueService.sendToQueue(
        'TriggerFetchSeatmap',
        this.fromCartIdAndHash(({ id, hash }: IdAndHash) =>
          this.ancillaryService.triggerFetchSeatMap(flightId, id, hash, currencyCode, cancelStream$)
        ),
        cancelStream$
      );
    }
  }

  queueServicesUpdate(
    request: FinnairServiceRequestItem | FinnairServiceRequestItem[],
    observe: PartialObserver<FinnairCart> = { complete: noop }
  ): void {
    const allRequests = Array.isArray(request) ? request : [request];
    this.queueService.cancelPayment.next();
    this.updatePartialCart(
      `${allRequests.map((r) => r.category).join('-')}Update`,
      (url, cancelStream$) => this.cartService.triggerUpdateServices(allRequests, url, cancelStream$),
      observe,
      ...allRequests.map((r) => getUpdateId(r.category, r.fragmentId))
    );
  }

  queueSeatSelectionReset(observe: PartialObserver<FinnairCart> = { complete: noop }): void {
    this.queueService.cancelPayment.next();

    const flights = snapshot(this.store$.pipe(cartFlights()));
    const flightIds: string[] = flights.map((key) => getUpdateId(SubCategory.SEAT, key.id));

    this.updatePartialCart(
      'seatSelectionReset',
      (url, cancelStream$) => this.cartService.triggerResetSeatSelections(url, cancelStream$),
      observe,
      ...flightIds
    );
  }

  private updatePartialCart(
    actionName: string,
    update: (cartUrl: string, cancelStream$: Observable<void>) => Observable<FinnairCart>,
    observe: PartialObserver<FinnairCart>,
    ...cartPartIds: string[]
  ): void {
    this.store$.dispatch(CartActions.isUpdatingPartialCart({ cartPartIds }));
    const cancelStream$ = this.queueService.toCancelEvent(this.cancelPaxUpdate$);

    this.queueService.sendToQueue(
      actionName,
      this.fromCartUrl((url: string) => {
        return update(url, cancelStream$).pipe(
          tap({
            complete: () => this.store$.dispatch(CartActions.clearUpdatingPartialCart({ cartPartIds })),
            error: () => this.store$.dispatch(CartActions.errorUpdatingPartialCart({ cartPartIds })),
          }),
          tap(observe),
          catchError(() => EMPTY)
        );
        // Should just complete the stream, booking cart service logs the error to sentry
      }),
      cancelStream$
    );
  }

  private fromCartState = (fn: (cart: CartState) => Observable<unknown>) =>
    this.store$.pipe(
      cartState(),
      filter((cart) => cart && !!cart.cartUrl),
      take(1),
      switchMap(fn)
    );

  private fromCartUrl = (fn: (url: string) => Observable<unknown>) =>
    this.store$.pipe(cartUrl(), filter(Boolean), take(1), switchMap(fn));

  private fromCartIdAndHash = (fn: (currentCartIdAndHash: IdAndHash) => Observable<unknown>) =>
    this.store$.pipe(
      cartIdAndHash(),
      filter(({ id, hash }: IdAndHash) => !!id && !!hash),
      take(1),
      switchMap((c) => fn(c))
    );
}
